import { useEffect, useImperativeHandle, useRef, type Ref } from "react";
import css from "./WheelColumn.module.css";

/**
 * WheelColumn — барабан TimePicker: физика портирована ДОСЛОВНО из
 * design-references/time-picker.js:55-92 (функция Wheel):
 *   - кольцевая виртуализация: 11 переиспользуемых нод, values[mod(idx,len)];
 *   - строка H=32, видимая зона 160 (5 строк), центр CTR=64 получает .c;
 *   - drag: EMA скорости vel=vel*.7+v*.3 (нормировка на кадр 16.7мс),
 *     кламп ±1.1, момент vel*=.90 до |vel|≤.045, затем snap;
 *   - snap/докрутка: ease-out 1-(1-k)^3, dur=min(650, 160+|d|*55);
 *   - tap-to-select: сдвиг <6px и <350мс — докрутка к строке под пальцем;
 *   - desktop wheel: один шаг на notch (native listener, passive:false);
 *   - setValue по кратчайшему пути вокруг кольца: d=mod(d+L/2,L)-L/2.
 *
 * SILENT-семантика (ключевая, эталон TP:66-70,129): onUserChange эмитится
 * ТОЛЬКО жестом пользователя (tap, snap после инерции, колесо мыши).
 * Программный setValue через handle — всегда silent, чтобы контролируемые
 * докрутки (init, смена сегмента, пресет, прямой ввод) не перезаписывали
 * state потребителя промежуточными значениями.
 */
export interface WheelHandle {
  /** Программная докрутка — всегда silent (onUserChange не эмитится). */
  setValue(v: string, animated: boolean): void;
  value(): string;
}

interface WheelColumnProps {
  values: string[];
  /** Стартовое значение при маунте (silent, без анимации). */
  initial: string;
  onUserChange: (v: string) => void;
  handleRef: Ref<WheelHandle>;
  ariaLabel?: string;
}

const H = 32;
const VIS = 11;
const CTR = 64;
const mod = (n: number, l: number) => ((n % l) + l) % l;

interface Engine {
  value(): string;
  setValue(v: string, animated: boolean, silent?: boolean): void;
  destroy(): void;
}

function createEngine(root: HTMLDivElement, values: string[], emit: (v: string) => void): Engine {
  let pos = 0;
  let anim: number | null = null;
  const items: HTMLDivElement[] = [];
  for (let i = 0; i < VIS; i++) {
    const d = document.createElement("div");
    if (css.item) d.className = css.item;
    root.appendChild(d);
    items.push(d);
  }
  function render(): void {
    const base = Math.round(pos);
    const frac = pos - base;
    for (let i = 0; i < VIS; i++) {
      const o = i - (VIS - 1) / 2;
      const el = items[i];
      if (!el) continue;
      el.textContent = values[mod(base + o, values.length)] ?? "";
      el.style.transform = `translateY(${CTR + (o - frac) * H}px)`;
      if (css.c) el.classList.toggle(css.c, o === 0 && Math.abs(frac) < 0.5);
    }
  }
  function stop(): void {
    if (anim !== null) {
      cancelAnimationFrame(anim);
      anim = null;
    }
  }
  function value(): string {
    return values[mod(Math.round(pos), values.length)] ?? "";
  }
  const done = () => emit(value());
  function animateTo(target: number, dur?: number, silent?: boolean): void {
    stop();
    const from = pos;
    const d = target - from;
    if (Math.abs(d) < 0.001) {
      pos = target;
      render();
      if (!silent) done();
      return;
    }
    const ms = dur ?? Math.min(650, 160 + Math.abs(d) * 55);
    const t0 = performance.now();
    const step = (t: number): void => {
      let k = Math.min(1, (t - t0) / ms);
      k = 1 - Math.pow(1 - k, 3);
      pos = from + d * k;
      render();
      if (k < 1) anim = requestAnimationFrame(step);
      else {
        anim = null;
        if (!silent) done();
      }
    };
    anim = requestAnimationFrame(step);
  }
  const snap = () => animateTo(Math.round(pos));
  function setValue(v: string, animated: boolean, silent?: boolean): void {
    let idx = values.indexOf(v);
    if (idx < 0) {
      const num = parseInt(v, 10);
      idx = 0;
      let best = Infinity;
      values.forEach((x, i) => {
        const dd = Math.abs(parseInt(x, 10) - num);
        if (dd < best) {
          best = dd;
          idx = i;
        }
      });
    }
    const L = values.length;
    let d = idx - mod(Math.round(pos), L);
    d = mod(d + L / 2, L) - L / 2; // кратчайший путь вокруг кольца
    if (animated) animateTo(Math.round(pos) + d, undefined, silent);
    else {
      stop();
      pos = Math.round(pos) + d;
      render();
      if (!silent) done();
    }
  }
  /* pointer-физика (эталон TP:78-89) */
  let py = 0;
  let pt = 0;
  let vel = 0;
  let moved = 0;
  let downT = 0;
  let dragging = false;
  const onDown = (e: PointerEvent): void => {
    dragging = true;
    stop();
    root.setPointerCapture(e.pointerId);
    py = e.clientY;
    pt = downT = performance.now();
    vel = 0;
    moved = 0;
  };
  const onMove = (e: PointerEvent): void => {
    if (!dragging) return;
    const t = performance.now();
    const dy = e.clientY - py;
    const dt = Math.max(1, t - pt);
    py = e.clientY;
    pt = t;
    moved += Math.abs(dy);
    pos -= dy / H;
    const v = -(dy / H) * (16.7 / dt);
    vel = vel * 0.7 + v * 0.3;
    render();
  };
  const onUp = (e: PointerEvent): void => {
    if (!dragging) return;
    dragging = false;
    if (moved < 6 && performance.now() - downT < 350) {
      /* tap-to-select: строка под пальцем, центр = индекс 2 */
      const r = root.getBoundingClientRect();
      const o = Math.floor((e.clientY - r.top) / H) - 2;
      animateTo(Math.round(pos) + o);
      return;
    }
    vel = Math.max(-1.1, Math.min(1.1, vel));
    const mom = (): void => {
      pos += vel;
      vel *= 0.9;
      render();
      if (Math.abs(vel) > 0.045) anim = requestAnimationFrame(mom);
      else {
        anim = null;
        snap();
      }
    };
    stop();
    anim = requestAnimationFrame(mom);
  };
  const onCancel = (): void => {
    if (dragging) {
      dragging = false;
      snap();
    }
  };
  const onWheel = (e: WheelEvent): void => {
    e.preventDefault();
    stop();
    pos += e.deltaY > 0 ? 1 : -1;
    animateTo(Math.round(pos));
  };
  root.addEventListener("pointerdown", onDown);
  root.addEventListener("pointermove", onMove);
  root.addEventListener("pointerup", onUp);
  root.addEventListener("pointercancel", onCancel);
  root.addEventListener("wheel", onWheel, { passive: false });
  render();
  return {
    value,
    setValue,
    destroy() {
      stop();
      root.removeEventListener("pointerdown", onDown);
      root.removeEventListener("pointermove", onMove);
      root.removeEventListener("pointerup", onUp);
      root.removeEventListener("pointercancel", onCancel);
      root.removeEventListener("wheel", onWheel);
      root.replaceChildren();
    },
  };
}

export function WheelColumn({ values, initial, onUserChange, handleRef, ariaLabel }: WheelColumnProps) {
  const rootRef = useRef<HTMLDivElement>(null);
  const engineRef = useRef<Engine | null>(null);
  const changeRef = useRef(onUserChange);
  changeRef.current = onUserChange;
  const initialRef = useRef(initial);
  initialRef.current = initial;

  useEffect(() => {
    const root = rootRef.current;
    if (!root) return;
    const engine = createEngine(root, values, (v) => changeRef.current(v));
    engine.setValue(initialRef.current, false, true);
    engineRef.current = engine;
    return () => {
      engine.destroy();
      engineRef.current = null;
    };
  }, [values]);

  useImperativeHandle(
    handleRef,
    () => ({
      setValue: (v: string, animated: boolean) => engineRef.current?.setValue(v, animated, true),
      value: () => engineRef.current?.value() ?? "",
    }),
    [],
  );

  return <div ref={rootRef} className={css.wheel} role="listbox" aria-label={ariaLabel} />;
}
